import {
  ClockCounterClockwiseIcon,
  FloppyDiskIcon,
  FlowArrowIcon,
  GearIcon,
  LightningIcon,
  PlusIcon,
  WarningCircleIcon,
  type Icon,
} from '@phosphor-icons/react';
import { useId } from 'react';
import { IconTooltip } from '@/components/common/IconTooltip';
import { useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { WorkbookTabBar } from './WorkbookTabBar';
import type { WorkbookEditor } from './useWorkbookEditor';
import { isSetupDiagnostic, useDiagnosticText } from './WorksheetDiagnostics';
import { UndoHistoryControls } from '@/components/common/UndoHistoryControls';
import { WorkbookRunButton } from './WorkbookRunButton';
import { WorkbookItemIcon } from './workbookItemIcon';
import { workbookItemIconKind } from './workbookItemIconKind';

/** The sidebar's icons for the tabs that are not Items. */
const PAGE_ICONS: Record<string, Icon> = {
  process: FlowArrowIcon,
  runs: ClockCounterClockwiseIcon,
  automations: LightningIcon,
  settings: GearIcon,
  versions: FloppyDiskIcon,
  'add-input': PlusIcon,
  'add-worksheet': PlusIcon,
  'add-pivot': PlusIcon,
};

/** A tab's icon: its Item's kind, as the sidebar shows it, or its page's. */
function TabIcon({
  definition,
  id,
}: {
  definition: WorkbookEditor['definition'];
  id: string;
}) {
  const kind = workbookItemIconKind(definition, id);
  if (kind)
    return (
      <WorkbookItemIcon
        kind={kind}
        className="size-3.5 shrink-0 text-muted-foreground"
        aria-hidden
      />
    );
  const PageIcon = PAGE_ICONS[id];
  return PageIcon ? (
    <PageIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
  ) : null;
}

// Inherited complexity: this branch only adds the WorkbookSampleRunMenu element (no branches added).
// fallow-ignore-next-line complexity
export function WorkbookToolbar({
  editor,
  opened,
  selected,
  preview,
  selectItem,
  keepItem,
  closeItem,
  busy,
  canUpdate,
  canExecute,
  act,
  openSettings,
}: {
  openSettings: () => void;
  editor: WorkbookEditor;
  opened: string[];
  selected: string;
  /** The temporary tab, if one is open. */
  preview: string | undefined;
  selectItem: (id: string) => void;
  keepItem: (id: string) => void;
  closeItem: (id: string) => void;
  busy: boolean;
  canUpdate: boolean;
  canExecute: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
}) {
  const gt = useGT();
  const { definition } = editor;
  const diagnosticText = useDiagnosticText(definition);
  const blockedId = useId();
  // An unfinished Item (a new pivot) blocks the Run as a next step, not an error.
  const errors = editor.plan.diagnostics.filter(
    (diagnostic) => !isSetupDiagnostic(diagnostic),
  );
  const shown = errors.length ? errors : editor.plan.diagnostics;
  const [firstDiagnostic] = shown;
  const blocked = firstDiagnostic && diagnosticText(firstDiagnostic);
  const blockedLabel = blocked
    ? [blocked.item, blocked.part, blocked.message].filter(Boolean).join(' · ')
    : '';
  const labels: Record<string, string> = {
    process: gt('Process', { context: 'workbook process' }),
    runs: gt('Runs', { context: 'workbook runs nav' }),
    automations: gt('Automations', { context: 'workbook automations nav' }),
    settings: gt('Settings', { context: 'workbook settings tab' }),
    versions: gt('Saved versions', { context: 'workbook versions nav' }),
    'add-input': gt('Add data', { context: 'workbook add input' }),
    'add-worksheet': gt('Add worksheet', { context: 'workbook add worksheet' }),
    'add-pivot': gt('Add pivot table', { context: 'workbook add pivot tab' }),
  };
  const items = [
    ...definition.files,
    ...definition.sources,
    ...definition.sheets,
    ...definition.outputs,
  ];
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b bg-muted/20 px-2">
      <WorkbookTabBar
        tabs={opened.map((id) => ({
          id,
          name: items.find((item) => item.id === id)?.name ?? labels[id] ?? id,
          icon: <TabIcon definition={definition} id={id} />,
        }))}
        selected={selected}
        preview={preview}
        select={selectItem}
        keep={keepItem}
        close={closeItem}
      />
      {canUpdate && (
        <UndoHistoryControls
          controls={editor.history}
          label={gt('Workbook edit history', {
            context: 'workbook undo history controls',
          })}
        />
      )}
      <IconTooltip>
        <Button
          size="icon"
          variant="ghost"
          className="size-7"
          disabled={busy || editor.saving || !canUpdate}
          onClick={() => void act(() => editor.save(true))}
          aria-label={gt('Save version', { context: 'workbook save version' })}
        >
          <FloppyDiskIcon className="size-4" />
        </Button>
      </IconTooltip>
      {blocked && (
        <span
          id={blockedId}
          role="status"
          className={`flex min-w-0 max-w-80 items-center gap-1 text-xs ${errors.length ? 'text-destructive' : 'text-muted-foreground'}`}
          title={blockedLabel}
        >
          <WarningCircleIcon className="size-4 shrink-0" />
          <span className="truncate">
            {shown.length > 1
              ? gt('{problem} (+{count} more)', {
                  context: 'workbook run blocked by diagnostics',
                  problem: blockedLabel,
                  count: String(shown.length - 1),
                })
              : blockedLabel}
          </span>
        </span>
      )}
      <WorkbookRunButton
        editor={editor}
        busy={busy}
        blockedBy={blocked ? blockedId : undefined}
        canUpdate={canUpdate}
        canExecute={canExecute}
        act={act}
      />
      <IconTooltip>
        <Button
          size="icon"
          variant="ghost"
          className="size-7"
          onClick={openSettings}
          aria-label={gt('Workbook settings', {
            context: 'workbook settings action',
          })}
        >
          <GearIcon className="size-4" />
        </Button>
      </IconTooltip>
    </div>
  );
}
